import { pluralFor } from '../plural';
import type { Messages } from './index';

const p = pluralFor('sl');

/**
 * Slovenian.
 *
 * <p>Maintained separately from Croatian, not derived from it. The two agree often
 * enough to be tempting and differ often enough to be embarrassing: *sledilnik* /
 * *pratilac*, *obvestila* / *obavijesti*, *nastavitve* / *postavke*. Shipping one to the
 * other's users reads as carelessness rather than as a near-miss.
 *
 * <p><strong>Every plural here carries a dual</strong>, because Slovenian has one and it
 * is ordinary speech, not an archaism: 1 sledilnik, 2 sledilnika, 3 sledilnika, 5
 * sledilnikov. `pluralFor('sl')` will not compile without `two`, which is the point —
 * a missing dual renders a real word in the wrong case, so nothing looks broken and
 * nobody catches it until a native speaker reads the screen.
 */
export const sl: Messages = {
  nav: {
    operate: 'Upravljanje',
    library: 'Vozni park',
    organization: 'Organizacija',
    fleetMap: 'Zemljevid',
    dashboard: 'Nadzorna plošča',
    trackers: 'Sledilniki',
    assets: 'Sredstva',
    trips: 'Vožnje',
    routes: 'Poti',
    geofences: 'Geoograje',
    alerts: 'Opozorila',
    users: 'Uporabniki',
    settings: 'Nastavitve',
    collapse: 'Skrči meni',
    expand: 'Razširi meni',
    main: 'Glavni',
    openMenu: 'Odpri meni',
    platform: 'Platforma',
    platformOrganizations: 'Organizacije',
    platformPlans: 'Paketi',
    platformAdmins: 'Skrbniki platforme',
  },

  common: {
    language: 'Jezik',
    theme: 'Tema',
    light: 'Svetla',
    dark: 'Temna',
    system: 'Sistemska',
    signOut: 'Odjava',
    cancel: 'Prekliči',
    save: 'Shrani',
    saving: 'Shranjujem…',
    retry: 'Poskusi znova',
    close: 'Zapri',
    search: 'Iskanje',
    clearFilter: 'Počisti filter',
    justNow: 'pravkar',
    never: 'Nikoli',
    none: '—',
    unknown: 'Neznano',
    actions: 'Dejanja',
    forbidden: 'Do te strani nimate dostopa',
    forbiddenBody: (role: string) =>
      `Vaša vloga je ${role}. Če potrebujete več dostopa, se obrnite na skrbnika.`,
    notFound: 'Strani ni mogoče najti',
    notFoundBody: 'Ta pot ne obstaja.',
    previous: 'Nazaj',
    next: 'Naprej',
    edit: (what: string) => `Uredi ${what}`,
    archive: (what: string) => `Arhiviraj ${what}`,
    archiveConfirm: (what: string) => `Arhiviram ${what}? Zgodovina se ohrani.`,
    pageOf: (page: string, total: string) => `Stran ${page} od ${total}`,
  },

  role: {
    OWNER: 'Lastnik',
    ADMIN: 'Skrbnik',
    DISPATCHER: 'Dispečer',
    VIEWER: 'Gledalec',
  },

  status: {
    moving: 'V vožnji',
    idle: 'Miruje',
    stale: 'Zastarelo',
    offline: 'Nedosegljiv',
    unreported: 'Še brez lokacije',
    alert: 'Opozorilo',
    lowBattery: 'Nizka baterija',
  },

  age: {
    justNow: 'pravkar',
    minutes: (n: string) => `pred ${n} min`,
    hours: (h: string, m: number) => (m === 0 ? `pred ${h} h` : `pred ${h} h ${m} min`),
    yesterday: 'včeraj',
    days: (n: number) =>
      p(n, {
        one: 'pred {n} dnevom',
        two: 'pred {n} dnevoma',
        few: 'pred {n} dnevi',
        other: 'pred {n} dnevi',
      }),
    inMinutes: (n: string) => `čez ${n} min`,
    inHours: (h: string, m: number) => (m === 0 ? `čez ${h} h` : `čez ${h} h ${m} min`),
    tomorrow: 'jutri',
    inDays: (n: number) =>
      p(n, {
        one: 'čez {n} dan',
        two: 'čez {n} dneva',
        few: 'čez {n} dni',
        other: 'čez {n} dni',
      }),
  },

  fleet: {
    title: 'Zemljevid voznega parka',
    listTitle: 'Vozni park',
    listRegion: 'Seznam voznega parka',
    showList: 'Pokaži seznam',
    hideList: 'Skrij seznam',
    searchPlaceholder: 'Ime ali ID naprave',
    legend: 'Legenda',
    noMatches: 'Ni zadetkov',
    noMatchesBody: 'Noben sledilnik ne ustreza trenutnemu filtru.',
    noPositions: 'Še ni lokacij',
    noTrackers: 'Ni sledilnikov',
    noPositionsBody: 'Registrirani sledilniki se pojavijo tukaj, ko sporočijo lokacijo.',
    noTrackersBody: 'Registrirajte sledilnik, da se prikaže na zemljevidu.',
    loadFailed: 'Voznega parka ni bilo mogoče naložiti.',

    unreportedCount: (n: number) =>
      p(n, {
        one: 'Registriran je {n} sledilnik, ki še ni sporočil lokacije.',
        two: 'Registrirana sta {n} sledilnika, nobeden še ni sporočil lokacije.',
        few: 'Registrirani so {n} sledilniki, nobeden še ni sporočil lokacije.',
        other: 'Registriranih je {n} sledilnikov, nobeden še ni sporočil lokacije.',
      }),

    currentTrip: 'Vožnja na zemljevidu',
    tripSince: (time: string) => `od ${time}`,
    noRecentTrip: 'V zadnjih 24 urah ni vožnje',
    fitAll: 'Prikaži vse sledilnike',

    detail: {
      speed: 'Hitrost',
      heading: 'Smer',
      battery: 'Baterija',
      satellites: 'Sateliti',
      position: 'Lokacija',
      recordedByDevice: 'Zabeležila naprava',
      receivedByServer: 'Prejel strežnik',
      deviceClock: 'Ura naprave',
      deviceClockUnset: 'Ni nastavljena — čas je strežnikov',
      deviceId: 'ID naprave',
      openAlert: 'Ta sledilnik ima odprto opozorilo.',
      historical:
        'To je zadnja sporočena lokacija, ne trenutna. Osenčen krog na zemljevidu kaže, kako daleč bi vozilo od takrat lahko prišlo.',
      approximate: (metres: string) =>
        `Približna lokacija glede na bližnje bazne postaje (± ${metres} m) — brez satelitskega signala. Vozilo je nekje znotraj osenčenega kroga.`,
      heldOnGnss: (age: string) =>
        `Trenutno brez satelitskega signala — ocena baznih postaj se še ujema z zadnjo GPS lokacijo (${age}), zato je prikazana tam.`,
    },
    tripLoading: 'Nalaganje poti …',

    lastFix: (age: string) => `zadnja lokacija ${age}`,
    noFixYet: 'še brez lokacije',
    battery: (percent: string) => `baterija ${percent}`,
    speed: (kmh: string) => `${kmh} km/h`,

    stacked: (n: number) =>
      p(n, {
        one: 'Tu je {n} sledilnik, pritisnite za razpostavitev',
        two: 'Tu sta {n} sledilnika, pritisnite za razpostavitev',
        few: 'Tu so {n} sledilniki, pritisnite za razpostavitev',
        other: 'Tu je {n} sledilnikov, pritisnite za razpostavitev',
      }),

    filterLabel: 'Filtriraj vozni park',
    shownOfTotal: (shown: string, total: string) => `Prikazanih ${shown} od ${total}`,

    matching: (n: number) =>
      p(n, {
        one: '{n} zadetek',
        two: '{n} zadetka',
        few: '{n} zadetki',
        other: '{n} zadetkov',
      }),
  },

  trips: {
    title: 'Vožnje',
    description: 'Vožnje, rekonstruirane iz sporočenih lokacij.',
    tracker: 'Sledilnik',
    chooseTracker: 'Izberite sledilnik',
    chooseTrackerPlaceholder: 'Izberite sledilnik …',
    chooseTrackerBody: 'Vožnje se rekonstruirajo za vsak sledilnik iz njegovih lokacij.',
    from: 'Od',
    to: 'Do',
    back: 'Vožnje',
    backLabel: 'Nazaj na seznam voženj',
    noTrips: 'V tem obdobju ni voženj',
    noTripsBody: 'Sledilnik je bodisi miroval bodisi v teh dneh ni sporočil ničesar.',
    loadFailed: 'Voženj ni bilo mogoče naložiti.',
    noneSelected: 'Nobena vožnja ni izbrana',
    noneSelectedBody: 'Izberite vožnjo na levi za predvajanje.',
    back1: 'Ena lokacija nazaj',
    forward1: 'Ena lokacija naprej',
    play: 'Predvajaj',
    pause: 'Premor',
    scrubberLabel: 'Položaj v vožnji, po lokacijah',
    fixOf: (index: string, total: string) => `${index} / ${total}`,

    distance: (km: string) => `${km} km med lokacijama`,

    fixes: (n: number) =>
      p(n, {
        one: '{n} lokacija',
        two: '{n} lokaciji',
        few: '{n} lokacije',
        other: '{n} lokacij',
      }),

    duration: (hours: number, minutes: number) =>
      hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`,

    maxSpeed: (kmh: string) => `najv. ${kmh} km/h`,

    filteredSegments: (n: number) =>
      p(n, {
        one: '{n} neverjeten skok ni vštet v razdaljo',
        two: '{n} neverjetna skoka nista všteta v razdaljo',
        few: '{n} neverjetni skoki niso všteti v razdaljo',
        other: '{n} neverjetnih skokov ni vštetih v razdaljo',
      }),
  },

  trackers: {
    title: 'Sledilniki',
    description: 'Sledilne naprave, registrirane v vaši organizaciji.',
    register: 'Registriraj',
    searchPlaceholder: 'Ime, ID naprave ali sredstvo',
    filterLabel: 'Filtriraj sledilnike na tej strani',
    tableCaption: 'Registrirani sledilniki',
    colTracker: 'Sledilnik',
    colAsset: 'Sredstvo',
    colLastSeen: 'Nazadnje viden',
    colBattery: 'Baterija',
    colInterval: 'Interval',
    colAlerts: 'Opozorila',
    unassigned: 'Nedodeljen',
    noMatches: 'Ni zadetkov',
    noMatchesBody: 'Noben sledilnik na tej strani ne ustreza filtru.',
    empty: 'Še ni sledilnikov',
    emptyBody: 'Registrirajte ID naprave, da začnete sprejemati njeno telemetrijo.',
    loadFailed: 'Sledilnikov ni bilo mogoče naložiti.',
    minutes: (n: string) => `${n} min`,
    count: (n: number) =>
      p(n, {
        one: '{n} sledilnik',
        two: '{n} sledilnika',
        few: '{n} sledilniki',
        other: '{n} sledilnikov',
      }),

    form: {
      name: 'Ime',
      nameHint: 'To bo iskal dispečer.',
      deviceId: 'ID naprave',
      interval: 'Interval poročanja',
      intervalHint: 'Sekunde med lokacijami. Pustite prazno za privzeto vrednost organizacije.',
      intervalElsewhere:
        'Interval poročanja in ostale nastavitve naprave so pod Nastavitve naprave.',
      deviceIdHint: 'Točno to, kar naprava objavlja na trackers/<device-id>/telemetry.',
      editTitle: 'Uredi sledilnik',
      createTitle: 'Registriraj sledilnik',
      editHint: 'ID naprave je po prevzemu nespremenljiv.',
      createHint: 'Prevzemite ID naprave v to organizacijo, da bo njena telemetrija sprejeta.',
    },
  },

  trackerConfig: {
    open: (name: string) => `Nastavitve naprave za ${name}`,
    title: 'Nastavitve naprave',
    description: (name: string) =>
      `Kako ${name} poroča lokacijo, išče satelitski signal in varčuje z energijo.`,
    readOnly: 'Nastavitve naprave lahko spremeni le skrbnik.',
    loadFailed: 'Nastavitev naprave ni bilo mogoče naložiti.',
    saveFailed: 'Nastavitev naprave ni bilo mogoče shraniti.',
    saved: 'Shranjeno.',
    override: 'Lastna vrednost',
    overrideLabel: (field: string) => `Lastna vrednost: ${field}`,
    inheritedLabel: 'Privzeto za organizacijo',
    inherited: (value: string) => `Privzeto za organizacijo: ${value}`,
    statusApplied: 'Uveljavljeno',
    statusPending: 'V čakanju',
    appliedHint: 'Sledilnik je potrdil, da deluje s temi nastavitvami.',
    pendingHint:
      'Uveljavi se ob naslednji povezavi sledilnika (v načinu spanja: v enem intervalu poročanja).',
    revision: (n: string) => `Revizija ${n}`,

    fields: {
      mode: {
        label: 'Način napajanja',
        hint: 'Spanje za sledilnike na baterijo, vedno vklopljen za sledilnike, priključene na vozilo.',
      },
      reportIntervalSec: {
        label: 'Interval poročanja',
        hint: 'Čas med lokacijami. V načinu spanja vsaj 1 min.',
      },
      gnssTimeoutColdSec: {
        label: 'Časovna omejitev GNSS, hladni zagon',
        hint: 'Kako dolgo iskati lokacijo po vklopu ali dolgem spanju, preden obupa.',
      },
      gnssTimeoutWarmSec: {
        label: 'Časovna omejitev GNSS, topli zagon',
        hint: 'Kako dolgo iskati lokacijo, ko je na voljo nedavna lokacija.',
      },
      gnssMaxHdop: {
        label: 'Največji HDOP',
        hint: 'Lokacije, manj natančne od te meje, se zavržejo. Nižja vrednost je strožja.',
      },
      lowBatteryCutoffMv: {
        label: 'Izklop pri nizki bateriji',
        hint: 'Pod to napetostjo sledilnik preneha poročati, da zaščiti baterijo.',
      },
      lowBatterySleepSec: {
        label: 'Spanje pri nizki bateriji',
        hint: 'Kako dolgo sledilnik spi, preden znova preveri baterijo.',
      },
      uploadEveryFixes: {
        label: 'Pošlji vsakih N položajev',
        hint: 'Način spanja: položaji se shranjujejo in pošljejo ob vsakem N-tem bujenju. Višje = daljše delovanje baterije, večja zakasnitev na zemljevidu.',
      },
    },

    mode: {
      SLEEP: 'Spanje (baterija)',
      ALWAYS_ON: 'Vedno vklopljen (napajanje vozila)',
    },
    modeHint: {
      SLEEP: 'Se zbudi, sporoči lokacijo in znova zaspi.',
      ALWAYS_ON: 'Ostane ves čas povezan.',
    },

    units: {
      seconds: (n: string) => `${n} s`,
      minutes: (n: string) => `${n} min`,
      hours: (n: string) => `${n} h`,
      millivolts: (n: string) => `${n} mV`,
      /** Beside an input that already holds the number. */
      secondsSymbol: 's',
      millivoltsSymbol: 'mV',
    },

    validation: {
      range: (min: string, max: string) => `Vnesite vrednost od ${min} do ${max}.`,
      wholeNumber: 'Vnesite celo število.',
      sleepInterval: 'Način spanja zahteva interval poročanja vsaj 1 min.',
    },

    errors: {
      defaultsIncomplete: 'Vsaka privzeta vrednost mora biti nastavljena.',
      defaultsConflict:
        'Eden od sledilnikov ima lastni interval poročanja, krajši od 1 min, in bi končal v načinu spanja. Najprej spremenite ta sledilnik.',
    },

    defaults: {
      title: 'Privzete nastavitve sledilnikov',
      description:
        'Vsi sledilniki uporabljajo te vrednosti, razen če v lastnih nastavitvah naprave določijo svojo. Spremembe pridejo na napravo ob njeni naslednji povezavi.',
      adminOnly: 'Privzete nastavitve sledilnikov lahko spremeni le skrbnik.',
      loadFailed: 'Privzetih nastavitev sledilnikov ni bilo mogoče naložiti.',
    },
  },

  assets: {
    title: 'Sredstva',
    description: 'Vozila in pošiljke, na katere so pritrjeni vaši sledilniki.',
    create: 'Novo sredstvo',
    searchPlaceholder: 'Ime, oznaka ali sledilnik',
    filterLabel: 'Filtriraj sredstva na tej strani',
    tableCaption: 'Sredstva',
    colAsset: 'Sredstvo',
    colKind: 'Vrsta',
    editTitle: 'Uredi sredstvo',

    kind: {
      TRUCK: 'Tovornjak',
      TRAILER: 'Priklopnik',
      CONTAINER: 'Zabojnik',
      SHIPMENT: 'Pošiljka',
      OTHER: 'Drugo',
    },
    colTracker: 'Sledilnik',
    loadFailed: 'Sredstev ni bilo mogoče naložiti.',

    form: {
      name: 'Ime',
      kind: 'Vrsta',
      reference: 'Oznaka',
      referenceHint: 'Registrska številka, številka pošiljke ali ID v ERP. Mora biti enolična.',
      notes: 'Opombe',
      hint: 'Vozilo, priklopnik ali pošiljka, na katero je pritrjen sledilnik.',
    },

    attach: {
      title: 'Pripni sledilnik',
      placeholder: 'Izberite sledilnik …',
      none: 'Ni prostih sledilnikov',
      noneBody: 'Vsak registriran sledilnik je že pripet na neko sredstvo.',
    },
  },

  alerts: {
    title: 'Opozorila',
    description: 'Nedosegljive naprave, prazne baterije in prehodi geoograj.',
    tabOpen: 'Odprta',
    tabAcknowledged: 'Potrjena',
    tabAll: 'Vsa',
    acknowledge: 'Videl sem',
    acknowledgeAllCount: (n: number) =>
      p(n, { one: 'Potrdi {n}', two: 'Potrdi {n}', few: 'Potrdi {n}', other: 'Potrdi {n}' }),
    acknowledgeShort: 'Potrdi',
    resolve: 'Zaključi',
    resolved: 'Zaključeno',
    severityCritical: 'Kritično',
    severityWarning: 'Opozorilo',
    severityInfo: 'Informacija',
    allClear: 'Vse v redu',
    allClearBody: 'Ni odprtih opozoril. Vsi sledilniki poročajo po pričakovanjih.',
    nothingHere: 'Tukaj ni ničesar',
    loadFailed: 'Opozoril ni bilo mogoče naložiti.',
  },

  dashboard: {
    title: 'Nadzorna plošča',
    description: 'Stanje voznega parka na hitro.',
    updated: 'Posodobljeno',
    reporting: 'Poroča',
    openAlerts: 'Odprta opozorila',
    fixesThisHour: 'Lokacije v tej uri',
    assets: 'Sredstva',
    trackerStatus: 'Stanje sledilnikov',
    battery: 'Baterija',
    online: 'Povezan',
    stale: 'Zastarelo',
    offline: 'Nedosegljiv',
    disabled: 'Onemogočen',
    neverReported: 'Še ni poročal',
    critical: 'Kritično',
    notReported: 'Ni podatka',
    loadFailed: 'Nadzorne plošče ni bilo mogoče naložiti.',
    low: 'Nizka',

    someOffline: (n: number) =>
      p(n, {
        one: '{n} nedosegljiv',
        two: '{n} nedosegljiva',
        few: '{n} nedosegljivi',
        other: '{n} nedosegljivih',
      }),
    allReporting: 'Vsi sledilniki poročajo',
    someCritical: (n: number) =>
      p(n, {
        one: '{n} kritično',
        two: '{n} kritični',
        few: '{n} kritična',
        other: '{n} kritičnih',
      }),
    raisedToday: (n: number) =>
      p(n, {
        one: '{n} sproženo danes',
        two: '{n} sproženi danes',
        few: '{n} sprožena danes',
        other: '{n} sproženih danes',
      }),
    inLast24h: (n: string) => `${n} v 24 h`,
    withoutTracker: (n: number) =>
      p(n, {
        one: '{n} brez sledilnika',
        two: '{n} brez sledilnika',
        few: '{n} brez sledilnika',
        other: '{n} brez sledilnika',
      }),
    allTracked: 'Vsa sredstva so sledena',
  },

  users: {
    title: 'Uporabniki',
    description: 'Kdo se lahko prijavi in kaj sme početi.',
    tableCaption: 'Člani organizacije',
    colMember: 'Član',
    colRole: 'Vloga',
    colStatus: 'Status',
    colLastSeen: 'Nazadnje viden',
    you: '(vi)',
    active: 'Aktiven',
    deactivated: 'Deaktiviran',
    empty: 'Ni članov',
    emptyBody: 'Povabite nekoga za začetek.',
    loadFailed: 'Članov ni bilo mogoče naložiti.',

    invitations: 'Povabila',
    invitationsHint: 'Brez povabila prijava ni mogoča, zato novi uporabniki dostop dobijo tako.',
    inviteEmailLabel: 'E-poštni naslov za povabilo',
    inviteEmailPlaceholder: 'ime@podjetje.si',
    invite: 'Povabi',
    revoke: 'Prekliči povabilo',
    expires: 'poteče',
  },

  routes: {
    title: 'Poti',
    description: 'Načrtovane vožnje in koridorji, zgrajeni iz njih.',
    new: 'Nova pot',
    empty: 'Ni še nobene poti',
    emptyBody:
      'Načrtujte vožnjo z začetnim in končnim naslovom, nato iz nje ustvarite koridor, ki opozori, ko vozilo zapusti pot.',
    loadFailed: 'Poti ni bilo mogoče naložiti.',
    name: 'Ime',
    namePlaceholder: 'Ljubljana → Koper',
    vehicle: 'Vozilo',
    chooseVehicle: 'Izberite vozilo…',
    noVehicle: 'Vozilo še ni določeno',
    start: 'Začetek',
    end: 'Cilj',
    stop: 'Postanek',
    addStop: 'Dodaj postanek',
    clearStart: 'Počisti začetek',
    clearEnd: 'Počisti cilj',
    unresolvedStart: 'Začetek še nima naslova.',
    unresolvedEnd: 'Cilj še nima naslova.',
    removeStop: (n: number) => `Odstrani postanek ${n}`,
    moveStopUp: (n: number) => `Premakni postanek ${n} naprej`,
    moveStopDown: (n: number) => `Premakni postanek ${n} nazaj`,
    addressPlaceholder: 'Naslov ali klik na karto',
    computing: 'Iskanje ceste …',
    computeFailed: 'Poti ni bilo mogoče izračunati.',
    noRoute: 'Med temi postanki ni vozne poti.',
    stopNotOnRoad: 'Eden od postankov ni blizu ceste. Premaknite ga bližje.',
    tooLong: 'Ta vožnja je predolga za eno pot. Razdelite jo na postanku.',
    unresolvedStop: (n: number) => `Postanek ${n} še nima naslova.`,
    snappedFar: (metres: string) => `${metres} m od najbližje ceste`,
    distance: (km: string) => `${km} km`,
    duration: (hours: number, minutes: number) =>
      hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`,
    stops: (n: number) =>
      p(n, {
        one: '{n} postanek',
        two: '{n} postanka',
        few: '{n} postanki',
        other: '{n} postankov',
      }),
    createCorridor: 'Ustvari koridor',
    hasCorridor: 'Koridor ustvarjen',
    corridorCreated: 'Koridor je ustvarjen iz te poti.',
    corridorExists: 'Iz te poti je koridor že ustvarjen.',
    needsVehicle: 'Pred ustvarjanjem koridorja določite vozilo.',
    accuracyNote: (metres: string) =>
      `Geometrija te poti je natančna na približno ${metres} m, zato je dejanska toleranca koridorja za toliko manjša.`,
    toleranceTooTight: 'Preozko za geometrijo te poti. Razširite jo.',
    corridorPoints: (n: number) =>
      p(n, {
        one: 'Koridor sledi {n} točki',
        two: 'Koridor sledi {n} točkama',
        few: 'Koridor sledi {n} točkam',
        other: 'Koridor sledi {n} točkam',
      }),
    deleteConfirm: (name: string) => `Arhiviram ${name}? Ustvarjeni koridor ostane.`,
    saveFailed: 'Poti ni bilo mogoče shraniti.',
    useThisRoute: 'Uporabi to pot',
    planRoute: 'Načrtuj iz naslovov',
  },

  address: {
    label: 'Naslov',
    placeholder: 'Poiščite naslov',
    searching: 'Iskanje …',
    noResults: 'Ni ujemajočega naslova',
    typeMore: (n: number) =>
      p(n, {
        one: 'Vnesite še {n} znak',
        two: 'Vnesite še {n} znaka',
        few: 'Vnesite še {n} znake',
        other: 'Vnesite še {n} znakov',
      }),
    unavailable: 'Iskanje naslovov ni na voljo. Kliknite na karto za postanek.',
    approximate: 'Približno',
  },

  geofences: {
    title: 'Geoograje',
    show: 'Pokaži geoograje',
    hide: 'Skrij geoograje',
    newArea: 'Novo območje',
    newCorridor: 'Nov koridor poti',
    empty: 'Ni geoograj',
    emptyBody:
      'Narišite območje okoli lokacije ali koridor poti, da boste obveščeni, ko vozilo zapusti načrtovano pot.',
    loadFailed: 'Geoograj ni bilo mogoče naložiti.',
    placeCentre: 'Kliknite na zemljevid, da postavite središče.',
    placeRoute: 'Klikajte vzdolž poti za dodajanje točk.',
    escToCancel: 'za preklic.',

    editor: {
      name: 'Ime',
      namePlaceholder: 'Skladišče Ljubljana',
      radius: (metres: string) => `Polmer · ${metres} m`,
      tolerance: (metres: string) => `Odstopanje · ${metres} m`,
      radiusHint: 'Merjeno na terenu, ne v slikovnih pikah.',
      appliesTo: 'Velja za',
      everything: 'Vse sledilnike',
      undoPoint: 'Razveljavi zadnjo točko',
      notifyEnter: 'Opozori ob prihodu',
      notifyExit: 'Opozori ob odhodu',
      notifyRejoin: 'Opozori, ko se vrne na pot',
      notifyDeviate: 'Opozori, ko zapusti pot',
      baselineNote: (kind: string) =>
        `Sledilniki, ki so ob nastanku že ${kind}, se zabeležijo brez opozorila.`,
      routeHintNeedMore: 'Kliknite na karto za dodajanje točk. Pot potrebuje vsaj dve.',
      routeHintExtend: 'Kliknite za podaljšanje poti.',
      toleranceHint: 'Kako daleč od poti še šteje kot na poti. Upoštevajte napako GPS.',
      scopeAssetHint: 'Sledi sredstvu, zato velja tudi ob zamenjavi sledilnika.',
      baselineInside: 'znotraj',
      baselineOffRoute: 'izven poti',
      deleteConfirm: (name: string) => `Izbrišem ${name}? Pretekli dogodki se ohranijo.`,
      pause: (name: string) => `Začasno ustavi ${name}`,
      resume: (name: string) => `Nadaljuj ${name}`,
      delete: (name: string) => `Izbriši ${name}`,
      paused: 'ustavljeno',
      saveFailed: 'Geoograje ni bilo mogoče shraniti.',
    },
  },

  settings: {
    title: 'Nastavitve',
    description: 'Pragovi, videz in vaš račun.',
    tabOrganization: 'Organizacija',
    tabTrackerDefaults: 'Privzete nastavitve naprav',
    tabAppearance: 'Videz',
    tabProfile: 'Profil',
    name: 'Ime',
    timeZone: 'Časovni pas',
    timeZoneHint: 'ID pasu IANA, na primer Europe/Ljubljana.',
    offlineAfter: 'Nedosegljiv po',
    offlineAfterHint: (duration: string) =>
      `Zamujenih oken poročanja. Pri trenutnem intervalu je to približno ${duration} tišine.`,
    lowBattery: 'Prag nizke baterije',
    lowBatteryHint: 'Milivolti, ne odstotki: naprava poroča napetost.',
    ownerOnly:
      'To lahko spremeni le lastnik. Skupaj določata, kdaj sledilnik velja za nedosegljivega.',
    saveFailed: 'Nastavitev ni bilo mogoče shraniti.',
    loadFailed: 'Nastavitev organizacije ni bilo mogoče naložiti.',
  },

  account: {
    signingIn: 'Prijavljanje',
    switching: 'Preklapljam organizacijo',
    loadFailed: 'Vašega računa ni bilo mogoče naložiti',
    loadFailedBody:
      'Prijava je uspela, vendar aplikacija ni mogla dostopiti do vašega profila. Težava običajno izgine sama od sebe.',
    notInvited: 'Povabila še niste prejeli',
    notInvitedBody: 'Prosite skrbnika, naj povabi ta e-poštni naslov.',
    noOrganization: 'Še brez organizacije',
    noOrganizationBody:
      'Vaš račun še ni del nobene organizacije. Prosite skrbnika za povabilo ali nas kontaktirajte glede paketa.',
    suspended: 'Ta organizacija je onemogočena',
    suspendedBody:
      'Njeni podatki niso na voljo, dokler je skrbnik platforme ponovno ne aktivira. Če menite, da gre za napako, se obrnite nanj.',
    continueElsewhere: 'Nadaljuj v drugi organizaciji',
  },

  switcher: {
    label: 'Zamenjaj organizacijo',
    heading: 'Vaše organizacije',
    current: (name: string) => `Trenutna organizacija: ${name}`,
    defaultBadge: 'Privzeta',
    setDefault: (name: string) => `Nastavi ${name} kot privzeto`,
    isDefault: (name: string) => `${name} je vaša privzeta organizacija`,
    platformAccess: 'Dostop platforme',
    allOrganizations: 'Vse organizacije…',
    setDefaultFailed: 'Privzete organizacije ni bilo mogoče spremeniti.',
  },

  limits: {
    unlimited: 'Neomejeno',
    seats: (used: string, limit: string) => `Uporabniki: ${used} / ${limit}`,
    trackers: (used: string, limit: string) => `Sledilniki: ${used} / ${limit}`,
    usage: (used: string, limit: string) => `${used} / ${limit}`,
    seatLimitReached:
      'Vsa uporabniška mesta v tej organizaciji so zasedena, vključno z odprtimi povabili. Najprej odstranite člana ali prekličite povabilo ali povprašajte o večjem paketu.',
    trackerLimitReached:
      'Organizacija je dosegla omejitev števila sledilnikov. Najprej arhivirajte sledilnik ali povprašajte o večjem paketu.',
  },

  platform: {
    status: { ACTIVE: 'Aktivna', SUSPENDED: 'Onemogočena' },
    billingPeriod: { MONTHLY: 'Mesečno', YEARLY: 'Letno' },
    perMonth: (price: string) => `${price} / mesec`,
    perYear: (price: string) => `${price} / leto`,
    noPlan: 'Brez paketa',
    customLimits: 'Lastne omejitve',
    unnamed: 'Brez imena',
    you: '(vi)',
    switchInto: 'Preklopi',
    switchIntoNamed: (name: string) => `Preklopi v ${name}`,
    open: (name: string) => `Odpri ${name}`,

    orgs: {
      title: 'Organizacije',
      description: 'Vse stranke na platformi, njihov paket in koliko ga porabijo.',
      create: 'Nova organizacija',
      searchPlaceholder: 'Ime ali oznaka',
      searchLabel: 'Iskanje organizacij',
      tableCaption: 'Organizacije',
      colOrganization: 'Organizacija',
      colPlan: 'Paket',
      colStatus: 'Status',
      colSeats: 'Uporabniki',
      colTrackers: 'Sledilniki',
      colCreated: 'Ustvarjena',
      empty: 'Še ni organizacij',
      emptyBody: 'Ustvarite prvo in povabite njenega lastnika.',
      noMatches: 'Ni zadetkov',
      noMatchesBody: 'Nobena organizacija ne ustreza iskanju.',
      loadFailed: 'Organizacij ni bilo mogoče naložiti.',
      count: (n: number) =>
        p(n, {
          one: '{n} organizacija',
          two: '{n} organizaciji',
          few: '{n} organizacije',
          other: '{n} organizacij',
        }),
    },

    orgForm: {
      createTitle: 'Nova organizacija',
      createHint: 'Ustvari organizacijo in pošlje povabila. Vsaj en povabljenec mora biti lastnik.',
      name: 'Ime',
      slug: 'Oznaka',
      slugHint: 'Male črke, števke in -. Pustite prazno, da se izpelje iz imena.',
      slugInvalid: 'Samo male črke, števke in -, na začetku črka ali števka.',
      slugTaken: 'Ta oznaka je že zasedena.',
      plan: 'Paket',
      planLimitsHint: 'Omejitve določa paket.',
      seatLimit: 'Omejitev uporabnikov',
      trackerLimit: 'Omejitev sledilnikov',
      unlimited: 'Neomejeno',
      limitInvalid: 'Vnesite celo število, enako ali večje od nič.',
      prefilled: 'Izpolnjeno iz paketa. Spremenite, da organizaciji določite lastne omejitve.',
      invites: 'Povabila',
      invitesHint: 'Pridružijo se, ko se prijavijo s tem naslovom.',
      inviteEmail: (n: number) => `E-pošta povabljenca ${n}`,
      inviteRole: (n: number) => `Vloga povabljenca ${n}`,
      addInvite: 'Dodaj povabljenca',
      removeInvite: (n: number) => `Odstrani povabljenca ${n}`,
      ownerRequired: 'Vsaj en povabljenec mora biti lastnik.',
      seatsTooFew: 'Izbrane omejitve imajo manj uporabniških mest, kot je povabil.',
      submit: 'Ustvari organizacijo',
      createFailed: 'Organizacije ni bilo mogoče ustvariti.',
    },

    orgDetail: {
      back: 'Organizacije',
      backLabel: 'Nazaj na vse organizacije',
      loadFailed: 'Te organizacije ni bilo mogoče naložiti.',
      created: (date: string) => `Ustvarjena ${date}`,
      settings: 'Nastavitve',
      notes: 'Opombe',
      notesHint: 'Vidijo jih samo skrbniki platforme.',
      status: 'Status',
      saved: 'Shranjeno.',
      saveFailed: 'Organizacije ni bilo mogoče shraniti.',
      usage: 'Poraba',
      seats: 'Uporabniki',
      trackers: 'Sledilniki',
      members: 'Člani',
      membersCaption: 'Člani te organizacije',
      membersEmpty: 'Še ni članov',
      membersEmptyBody: 'Prikažejo se, ko sprejmejo povabilo.',
      memberCount: (n: number) =>
        p(n, { one: '{n} član', two: '{n} člana', few: '{n} člani', other: '{n} članov' }),
      invitations: 'Povabila',
      pendingCount: (n: number) =>
        p(n, {
          one: '{n} odprto povabilo',
          two: '{n} odprti povabili',
          few: '{n} odprta povabila',
          other: '{n} odprtih povabil',
        }),
      noPending: 'Ni odprtih povabil.',
      deactivate: (name: string) => `Deaktiviraj ${name}`,
      reactivate: (name: string) => `Ponovno aktiviraj ${name}`,
      lastOwner: 'Organizacija mora obdržati vsaj enega aktivnega lastnika.',
      inviteAlreadyPending: 'Ta naslov že ima odprto povabilo.',
      alreadyMember: 'Ta oseba je že članica.',
      memberUpdateFailed: 'Člana ni bilo mogoče posodobiti.',
      suspendTitle: (name: string) => `Onemogočim ${name}?`,
      suspendBody:
        'Njeni člani izgubijo dostop do vseh zaslonov in API-ja, dokler je ponovno ne aktivirate. Sledilniki še naprej pošiljajo podatke in nič se ne izbriše.',
      suspendConfirm: 'Onemogoči',
    },

    plans: {
      title: 'Paketi',
      description: 'Na kaj je lahko organizacija naročena in kakšne omejitve to prinaša.',
      create: 'Nov paket',
      tableCaption: 'Paketi',
      colPlan: 'Paket',
      colPrice: 'Cena',
      colSeats: 'Uporabniki',
      colTrackers: 'Sledilniki',
      colOrganizations: 'Organizacije',
      colStatus: 'Status',
      active: 'Aktiven',
      inactive: 'Neaktiven',
      empty: 'Še ni paketov',
      emptyBody: 'Določite paket, da organizacijam dodelite ceno in omejitve.',
      loadFailed: 'Paketov ni bilo mogoče naložiti.',
      edit: (name: string) => `Uredi ${name}`,
      usedBy: (n: number) =>
        p(n, {
          one: '{n} organizacija',
          two: '{n} organizaciji',
          few: '{n} organizacije',
          other: '{n} organizacij',
        }),
    },

    planForm: {
      createTitle: 'Nov paket',
      editTitle: 'Uredi paket',
      hint: 'Omejitve veljajo za vse organizacije s tem paketom.',
      code: 'Koda',
      codeHint: 'Stalni identifikator: male črke, števke, - in _.',
      codeInvalid: 'Samo male črke, števke, - in _, na začetku črka ali števka.',
      codeTaken: 'Ta koda je že zasedena.',
      name: 'Ime',
      description: 'Opis',
      price: 'Cena',
      priceHint: 'V spodnji valuti, na primer 49,00.',
      priceInvalid: 'Vnesite znesek, enak ali večji od nič, z največ dvema decimalkama.',
      currency: 'Valuta',
      currencyInvalid: 'Tri velike črke, na primer EUR.',
      billingPeriod: 'Obračunsko obdobje',
      maxSeats: 'Uporabniki',
      maxTrackers: 'Sledilniki',
      active: 'Na voljo',
      activeHint: 'Neaktiven paket ostane pri organizacijah, ki ga že imajo.',
      saveFailed: 'Paketa ni bilo mogoče shraniti.',
    },

    admins: {
      title: 'Skrbniki platforme',
      description: 'Osebe, ki lahko upravljajo vse organizacije, pakete in omejitve.',
      add: 'Dodaj skrbnika platforme',
      addHint: 'Najdete lahko samo osebe, ki so se vsaj enkrat prijavile.',
      tableCaption: 'Skrbniki platforme',
      colPerson: 'Oseba',
      colSince: 'Račun ustvarjen',
      revoke: (name: string) => `Odvzemi skrbniške pravice osebi ${name}`,
      revokeTitle: (name: string) => `Odvzamem pravice osebi ${name}?`,
      revokeBody: 'Članstva v organizacijah ohrani, platforme pa ne more več upravljati.',
      revokeConfirm: 'Odvzemi',
      cannotRevokeSelf: 'Svojega dostopa do platforme ne morete odvzeti.',
      lastAdmin: 'Platforma mora imeti vsaj enega skrbnika.',
      actionFailed: 'Dostopa do platforme ni bilo mogoče spremeniti.',
      loadFailed: 'Skrbnikov platforme ni bilo mogoče naložiti.',
      count: (n: number) =>
        p(n, {
          one: '{n} skrbnik',
          two: '{n} skrbnika',
          few: '{n} skrbniki',
          other: '{n} skrbnikov',
        }),
      searchLabel: 'Iskanje oseb, ki so se prijavile',
      searchPlaceholder: 'Ime ali e-pošta',
      noResults: 'Iskanju ne ustreza nihče.',
      grant: 'Dodeli',
      grantNamed: (name: string) => `Dodeli skrbniške pravice osebi ${name}`,
      alreadyAdmin: 'Že skrbnik',
    },
  },

  validation: {
    required: 'Obvezno.',
    minChars: (n: number) =>
      p(n, {
        one: 'Vsaj {n} znak.',
        two: 'Vsaj {n} znaka.',
        few: 'Vsaj {n} znaki.',
        other: 'Vsaj {n} znakov.',
      }),
    maxChars: (n: number) =>
      p(n, {
        one: 'Največ {n} znak.',
        two: 'Največ {n} znaka.',
        few: 'Največ {n} znaki.',
        other: 'Največ {n} znakov.',
      }),
    minSeconds: (n: number) =>
      p(n, {
        one: 'Vsaj {n} sekunda.',
        two: 'Vsaj {n} sekundi.',
        few: 'Vsaj {n} sekunde.',
        other: 'Vsaj {n} sekund.',
      }),
    deviceIdChars: 'Samo črke, števke in . _ : -',
    email: 'Vnesite veljaven e-poštni naslov.',
  },
};
